import { styled } from '@mui/material/styles';
import { css } from '@emotion/react';
import { UiCard } from 'ui-components';
import { marginRight } from 'utils';

export const StyledBarLineChart = styled(UiCard)(
  ({ theme }) =>
    css`
      &.full-screen {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background-color: ${theme.palette.common.white};
        z-index: 20;
        .card-body {
          height: 80vh;
          padding: 50px;
        }
      }
      .card-header {
        padding: 0 24px;
        justify-content: space-between;
        .actions {
          .icon {
            ${marginRight('26px')}
            cursor: pointer;
          }
        }
        .title {
          font-size: ${theme.typography.subtitle2.fontSize};
          font-weight: ${theme.typography.subtitle2.fontWeight};
          line-height: ${theme.typography.subtitle2.lineHeight};
          color: ${theme.palette.gray.darkest};
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
      .card-body {
        justify-content: center;
        background-color: white;
        border-radius: 0 0 8px 8px;
        padding: 20px;
        overflow: hidden;
        height: 424px;
        .custom-loading,
        .no-data,
        .display-errors {
          height: 80%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .display-errors {
          flex-direction: column;
        }
        .recharts-wrapper {
          .recharts-legend-wrapper {
            bottom: -10px !important;
          }

          /*
           * Axis ticks are plain SVG <text>, which inherits this page's RTL
           * direction. For a signed value (e.g. "-50") that reorders the
           * glyphs so the sign lands after the digits ("50-"). Forcing
           * direction:ltr "fixes" the order but also flips how the axis's
           * text-anchor:end resolves, which pushes every tick (not just
           * signed ones) out of its reserved gutter so it never paints.
           * unicode-bidi:plaintext reorders the run based on its own
           * content instead of the ambient direction, without touching
           * direction itself, so text-anchor keeps resolving correctly and
           * the ticks stay visible.
           */
          svg text {
            unicode-bidi: plaintext;
          }
        }
        /*
         * The chart shares the card body with an optional custom filter row. It
         * has to take only the space left over (and be allowed to shrink),
         * otherwise its 100% height overflows the body and the x-axis and
         * legend are clipped, which is most visible in full screen. Unless a
         * page turns the body into a column, it stays a centered row, so the
         * container has to stretch or the chart gets no height at all.
         */
        .chart-container {
          flex: 1;
          align-self: stretch;
          min-width: 0;
          min-height: 0;
          width: 100%;
        }
        svg {
          outline: none;
        }
      }
    `
);

export const StyledCustomLegend = styled('div')(
  () => css`
    display: flex;
    flex-wrap: wrap;
    column-gap: 20px;
    row-gap: 4px;
    align-items: flex-end;
    font-size: 12px;
    justify-content: center;
  `
);

export const StyledLabelItem = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: flex-center;
    justify-content: space-between;
    gap: 8px;
    white-space: nowrap;
    .title {
      font-size: ${theme.typography.overline.fontSize};
      font-weight: ${theme.typography.overline.fontWeight};
      line-height: ${theme.typography.overline.lineHeight};
      color: #0c395a;
    }
    .label {
      width: 16px;
      height: 4px;
      display: inline-block;
    }
    .value {
      font-size: ${theme.typography.overline.fontSize};
      font-weight: ${theme.typography.overline.fontWeight};
      line-height: ${theme.typography.overline.lineHeight};
      color: #0c395a;
      /*
       * This label is shared by the legend and the tooltip, both of which can
       * show the signed NSS score. Plain "direction: ltr" has no bidi effect
       * on an inline element unless paired with unicode-bidi, so without this
       * a negative value's sign was still resolved under the ambient RTL
       * context and landed on the wrong side of the digits.
       */
      direction: ltr;
      unicode-bidi: embed;
    }
  `
);

export const StyledCustomTooltip = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: ${theme.palette.secondary.lightest};
    padding: 12px;
    border-radius: 8px;
    .title {
      text-align: right;
      font-size: ${theme.typography.overline.fontSize};
      font-weight: ${theme.typography.overline.fontWeight};
      line-height: ${theme.typography.overline.lineHeight};
      color: ${theme.palette.secondary.lighter};
    }
  `
);
